import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { encodeUrl, decodeUrl } from "./urlCodec";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Encodes text into URL-safe format (percent-encoding) or decodes an encoded URL back into readable text.`,
  how: `Paste your text or encoded URL, select encode or decode, and the converted result appears immediately.`,
  tip: `URL encoding matters most for characters like spaces, ampersands, and special symbols that have specific meaning in a URL's structure — plain alphanumeric text generally doesn't need encoding at all.`,
};

export default function UrlEncoder() {
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [fullUrl, setFullUrl] = useState(false); // encodeURI/decodeURI keep : / ? & = # intact
  const [plusSpace, setPlusSpace] = useState(true); // decode "+" as a space (form encoding)

  const [error, setError] = useState("");

  const { logUsageNow } = useLogUsageOnAction();

  return (
    <ToolContainer>
      <div className="space-y-4">
        <div><Label>Input</Label><Textarea aria-label="Input" value={input} onChange={e => { setInput(e.target.value); setError(""); }} placeholder="Enter URL or text..." className="font-mono" aria-invalid={!!error} />{error && <p role="alert" className="text-xs text-destructive mt-1">{error}</p>}</div>
        <div className="space-y-1 text-sm">
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={fullUrl} onChange={e => setFullUrl(e.target.checked)} /> Whole URL mode (keep : / ? &amp; = # as they are)</label>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={plusSpace} onChange={e => setPlusSpace(e.target.checked)} /> Decode + as a space (form data)</label>
        </div>
        <div className="flex gap-2">
          <Button onClick={async () => { setError(""); const r = encodeUrl(input, fullUrl); if (!r.ok) { setError(r.error); return; } const allowed = await logUsageNow(); if (!allowed) return; setOutput(r.value); }}>Encode</Button>
          <Button variant="outline" onClick={async () => { setError(""); const r = decodeUrl(input, fullUrl, plusSpace); if (!r.ok) { setError(r.error); return; } const allowed = await logUsageNow(); if (!allowed) return; setOutput(r.value); }}>Decode</Button>
        </div>
        {output && (
          <div><Label>Output</Label><Textarea aria-label="Output" value={output} readOnly className="font-mono" />
            <Button size="sm" className="mt-2" onClick={() => { void copyText(output, "Copied!"); }}>Copy</Button>
          </div>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
